import * as React from 'react';
import {createView, createViewConfig, view} from '@sail/engine';
import {search} from '@sail/icons/react/Icon';
import {tokens as $} from '@sail/tokens';
import type {View} from '@sail/engine';
import type {IconAsset} from '@sail/icons/types';
import {Icon} from './Icon';
import {css} from '../css/css';

type TitleOrDescription =
  | {
      title?: React.ReactNode;
      description: React.ReactNode;
    }
  | {
      title: React.ReactNode;
      description?: React.ReactNode;
    };

export type EmptyStateProps = View.IntrinsicElement<
  'div',
  TitleOrDescription & {
    actions?: React.ReactNode;
    /** Not shown for the small size. Defaults to the `search` icon if none is provided. */
    icon?: IconAsset;
    /** Medium sized empty states are best suited for content areas that take up a large part of the page. The small size is for cases where there may be many empty sections on a single page. */
    size?: 'small' | 'medium';
    subviews?: View.Subviews<{
      title: 'div';
      description: 'div';
      icon: typeof Icon;
      actions: 'div';
    }>;
  }
>;

export const EmptyStateConfig = createViewConfig({
  props: {} as EmptyStateProps,
  name: 'EmptyState',
  defaults: {
    size: 'medium',
  },
});

const DashedBorderSvg = createView((props) => {
  return (
    <view.svg
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="presentation"
      css={{pointerEvents: 'none'}}
      {...props}
    >
      <view.rect
        width="100%"
        height="100%"
        fill="none"
        rx="8"
        ry="8"
        stroke={$['color.border']}
        strokeWidth="1.5"
        strokeDasharray="8, 8"
        strokeDashoffset="4"
        strokeLinecap="round"
        css={{
          width: 'calc(100% - 3px)',
          height: 'calc(100% - 2px)',
          transform: 'translateY(1px) translateX(1px)',
        }}
      />
    </view.svg>
  );
});

export const EmptyState = EmptyStateConfig.createView(
  ({title, description, actions, icon, size, subviews, children, ...rest}) => {
    return (
      <view.div
        css={[
          css({
            textWrap: 'pretty',
          }),
          size === 'small'
            ? css({
                textAlign: 'center',
                minHeight: 'none',
                padding: 'xlarge',
                borderRadius: 'medium',
                position: 'relative',
              })
            : css({
                textAlign: 'left',
                minHeight: '464px',
                padding: 'xxlarge',
                backgroundImage: 'none',
                borderRadius: 'none',
              }),
        ]}
        {...rest}
      >
        {size === 'small' ? (
          <DashedBorderSvg css={{position: 'absolute', inset: 'space.0'}} />
        ) : null}
        <view.div
          css={[
            css({
              alignY: 'center',
              stack: 'y',
              gap: 'medium',
              width: '448px',
              maxWidth: 'fill',
            }),
            size === 'small' ? css({alignX: 'center'}) : css({alignX: 'start'}),
          ]}
        >
          {size !== 'small' ? (
            <Icon
              icon={icon || search}
              size="medium"
              appearance="contained"
              css={{color: 'subdued'}}
              inherits={subviews.icon}
            />
          ) : null}

          <view.div css={{stack: 'y', gap: 'xsmall'}}>
            {title ? (
              <view.div
                css={[
                  size === 'small'
                    ? css({
                        font: 'body.small',
                        color: 'subdued',
                      })
                    : css({
                        font: 'heading.medium',
                        color: 'emphasized',
                      }),
                ]}
                inherits={subviews.title}
              >
                {title}
              </view.div>
            ) : null}

            {description ? (
              <view.div inherits={subviews.description}>{description}</view.div>
            ) : null}
          </view.div>

          {actions ? (
            <view.div inherits={subviews.actions}>{actions}</view.div>
          ) : null}

          {children}
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      stack: 'y',
      alignY: 'center',
      alignX: 'center',
      width: 'fill',
    },
    subviews: {
      description: {
        css: {
          font: 'body.small',
          color: 'subdued',
        },
      },
      actions: {
        css: {
          font: 'label.medium',
        },
      },
    },
  },
);
